import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarClock, Menu, X } from "lucide-react";
import ThemeToggle from "./ThemeToggle";
import LanguageToggle from "./LanguageToggle";
import { scrollToFlow } from "./HowWeWorkMenu";
import DirArrow from "../ui/DirArrow";
import { EASE } from "../ui/Reveal";
import { ICON_BUTTON } from "../ui/styles";
import useLang from "../../hooks/useLang";

// Phone and tablet menu (below md): every page in one sheet under the bar,
// plus the theme and language switches that don't fit in the bar on small screens.
const MobileMenu = () => {
  const { t, dir } = useLang();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  // Close on navigation, and keep the page behind from scrolling while open.
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = overflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const links = [
    { label: t("howWeWork.badge"), onClick: () => scrollToFlow(navigate, pathname) },
    { label: t("nav.services"), to: "/services" },
    { label: t("nav.projects"), to: "/projects" },
    { label: t("nav.behindTheLight"), to: "/about" },
    { label: t("nav.careers"), to: "/careers" },
    { label: t("nav.contact"), to: "/contact" },
  ];

  const row =
    "group flex items-center gap-4 border-b border-white/10 py-4 text-2xl font-extrabold tracking-[-0.02em] text-white hover:text-rose-light transition-colors";

  return (
    <div className="md:hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={open ? "Close menu" : "Open menu"}
        className={ICON_BUTTON}
      >
        {open ? <X size={18} /> : <Menu size={18} />}
      </button>

      {/* Portaled to <body>: the bar's backdrop blur would otherwise trap a fixed sheet inside it. */}
      {open &&
        createPortal(
        <motion.div
          className="md:hidden fixed inset-x-0 top-[72px] bottom-0 z-40 overflow-y-auto bg-ink"
          dir={dir}
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: EASE }}
        >
          <nav className="container-site py-4">
            <ul>
              {links.map((l, i) => {
                const inner = (
                  <>
                    <span className="font-mono text-[11px] tracking-[0.16em] text-rose-light">0{i + 1}</span>
                    <span className="flex-1 text-start">{l.label}</span>
                    <DirArrow size={20} nudge className="text-white/40 group-hover:text-rose-light" />
                  </>
                );
                return (
                  <li key={l.label}>
                    {l.to ? (
                      <Link to={l.to} className={row}>
                        {inner}
                      </Link>
                    ) : (
                      <button
                        type="button"
                        className={`${row} w-full`}
                        onClick={() => {
                          setOpen(false);
                          l.onClick();
                        }}
                      >
                        {inner}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/contact?mode=call" className="btn-rose flex-1 justify-center">
                <CalendarClock size={16} /> {t("cta.bookCall")}
              </Link>
              <ThemeToggle />
              <LanguageToggle icon />
            </div>
          </nav>
        </motion.div>,
          document.body
        )}
    </div>
  );
};

export default MobileMenu;
